import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Switch } from '@mui/material';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { styled } from '@mui/material/styles';
import CustomCheckbox from '../CustomCheckbox';
import { toggleBranchExposure } from '../../api/exposure';
import { errorMessages } from '../../utils/methods';
import { appBrandingTypes } from '../../constants/constants';
// 기능 사용 여부 표시(#189)
const featureUseLabel = yn => (yn === 'Y' ? '사용' : '-');
const brandingLabel = type =>
  type && type !== 'NONE' ? appBrandingTypes.find(option => option.value === type)?.label || type : '-';


const STATUS_STYLES = {
  APPROVED: { color: '#0F7F85', backgroundColor: '#e6f5f5' },
  APPROVAL_PENDING: { color: '#5A5A5A', backgroundColor: '#f0f0f0' },
  REFUSED: { color: '#FFB659', backgroundColor: '#fff5e6' },
  WITHDREW: { color: '#EE735A', backgroundColor: '#fef0ed' },
};

const StyledHeaderCell = styled(TableCell)({
  fontWeight: 600,
  fontSize: '13px',
  color: '#333',
  padding: '10px 8px',
  borderRight: '1px solid #ddd',
  '&:last-child': {
    borderRight: 'none',
  },
});

const StyledBodyRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'inactive',
})(({ inactive }) => ({
  height: '44px',
  textDecoration: 'none',
  cursor: 'pointer',
  ...(inactive && {
    backgroundColor: '#f9f9f9',
    '& td': { color: '#bbb' },
  }),
  '&:hover': {
    backgroundColor: inactive ? '#f3f3f3' : '#f9fafb',
  },
}));

const StyledBodyCell = styled(TableCell)({
  fontSize: '13px',
  color: '#333',
  padding: '8px',
  borderRight: '1px solid #f0f0f0',
  '&:last-child': {
    borderRight: 'none',
  },
});

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
};

const StatusBadge = styled('span')(({ status }) => {
  const style = STATUS_STYLES[status] || { color: '#333', backgroundColor: '#f5f5f5' };
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    color: style.color,
    backgroundColor: style.backgroundColor,
  };
});

const CorpManagementTable = ({ managers, setManagers, handleChange, onRowClick }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const user = useSelector(state => state.user.user);

  const handleRowClick = (e, managerId) => {
    e.preventDefault();
    onRowClick?.();
    navigate(`/corp/${managerId}`);
  };

  const handleExposureToggle = async (e, manager) => {
    e.stopPropagation();
    const branchId = manager.centerId || manager.branchId || manager.headBranchId;
    if (!branchId) {
      toast.error('센터 정보가 없습니다');
      return;
    }
    const newValue = manager.exposureYn === 'Y' ? 'N' : 'Y';

    // Optimistic update
    setManagers(prev => ({
      ...prev,
      content: prev.content.map(m =>
        m.id === manager.id ? { ...m, exposureYn: newValue } : m,
      ),
    }));

    try {
      await toggleBranchExposure(user.token, branchId, newValue);
      toast.success(newValue === 'Y' ? '앱에 노출됩니다' : '앱에서 숨김 처리됩니다');
    } catch (error) {
      // Rollback
      setManagers(prev => ({
        ...prev,
        content: prev.content.map(m =>
          m.id === manager.id ? { ...m, exposureYn: manager.exposureYn } : m,
        ),
      }));
      toast.error(errorMessages(error));
    }
  };

  return (
    <TableContainer sx={{ mt: '10px', border: '1px solid #e8e8e8', borderRadius: '8px' }}>
      <Table size="small">
        <TableHead sx={{ background: '#F0F2F5' }}>
          <TableRow sx={{ height: '44px' }}>
            <StyledHeaderCell align="center" sx={{ width: '48px' }}>
              <CustomCheckbox
                color="primary"
                name="allSelect"
                checked={!managers?.content?.some(manager => manager.isChecked !== true)}
                onChange={handleChange}
              />
            </StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Number')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Business name')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('ID')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Nickname')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Phone number')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Date of joining')}</StyledHeaderCell>
            {/* <StyledHeaderCell align="center">앱 노출</StyledHeaderCell> */}
            <StyledHeaderCell align="center">IoT</StyledHeaderCell>
            <StyledHeaderCell align="center">안면인식</StyledHeaderCell>
            <StyledHeaderCell align="center">자동 차감</StyledHeaderCell>
            <StyledHeaderCell align="center">브랜딩</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('State')}</StyledHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {managers?.content?.map((manager, index) => {
            const rowNumber = (managers.number - 1) * managers.size + index + 1;

            return (
              <StyledBodyRow key={index} inactive={manager.status !== 'APPROVED'} onClick={e => handleRowClick(e, manager.id)}>
                <StyledBodyCell align="center" onClick={e => e.stopPropagation()}>
                  <CustomCheckbox
                    name={manager.username + manager.id}
                    checked={manager?.isChecked || false}
                    onChange={handleChange}
                  />
                </StyledBodyCell>
                <StyledBodyCell align="center">{rowNumber}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.businessName || '-'}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.username || '-'}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.name || '-'}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.phoneNumber || '-'}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.registeredDate || '-'}</StyledBodyCell>
                {/* <StyledBodyCell align="center" onClick={e => e.stopPropagation()}>
                  <Switch
                    size="small"
                    checked={manager.exposureYn === 'Y'}
                    onChange={e => handleExposureToggle(e, manager)}
                    sx={switchSx}
                  />
                </StyledBodyCell> */}
                <StyledBodyCell align="center">{featureUseLabel(manager.facilityControlYn)}</StyledBodyCell>
                <StyledBodyCell align="center">{featureUseLabel(manager.doorSystemYn)}</StyledBodyCell>
                <StyledBodyCell align="center">{featureUseLabel(manager.autoDeductionYn)}</StyledBodyCell>
                <StyledBodyCell align="center">{brandingLabel(manager.appBrandingType)}</StyledBodyCell>
                <StyledBodyCell align="center">
                  {manager.status ? (
                    <StatusBadge status={manager.status}>{t(manager.status)}</StatusBadge>
                  ) : '-'}
                </StyledBodyCell>
              </StyledBodyRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CorpManagementTable;
